import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Button, DialogActions } from '@mui/material';

import { removeDialog } from 'companion-app-components/flux/root-ui/rootUiActions';
import { 
  AccountGroup,
  AccountGroupWithAccess,
  DatasetShareAccessLevel,
} from 'companion-app-components/flux/account-groups/accountGroupsTypes';

import { 
  getAccountGroups,
} from 'companion-app-components/flux/account-groups/accountGroupsSelectors';
import { datasetSharesActions } from 'companion-app-components/flux/dataset-shares';
import { DatasetShare, mkDatasetShare } from 'companion-app-components/flux/dataset-shares/datasetSharesTypes';
import { authSelectors } from 'companion-app-components/flux/auth';
import { datasetsSelectors } from 'companion-app-components/flux/datasets';
import { hasCFOEntitlementForDataset } from 'companion-app-components/flux/entitlements/entitlementsUtils';
import { tracker } from 'companion-app-components/utils/core';

import AccountGroupsShare from 'components/AccountGroupsShare';

import AccessTypes from './AccessTypes';
import { DIALOG_TYPE_ACCOUNT_SHARE } from './actions';
import { getCFOEntityAccessTrackingProps, getItemAccessControl, hasDatasetFullAccess } from './components/utils';
import { styles } from './styles';

const useStyles = makeStyles()(styles as Record<string, any>);

interface Props {
  share: DatasetShare;
}

const ManageAccountShare: FC<Props> = ({ share }) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  
  const accountGroups = useSelector(getAccountGroups);
  const datasetId = useSelector(authSelectors.getDatasetId);
  const datasetsById = useSelector(datasetsSelectors.getDatasetsById);
  const datasetEntitlements = datasetsById?.get(datasetId)?.entitlements;
  const isCFOUser = hasCFOEntitlementForDataset(datasetEntitlements);

  const [accountGroupsState, setAccountGroupsState] = useState<AccountGroupWithAccess[]>([]);
  const [fullAccess, setFullAccess] = useState(hasDatasetFullAccess(accountGroups, share));

  useEffect(() => {
    if (!accountGroups) {
      return;
    }
    const { read, write } = share.itemAccessControl?.resources.ACCOUNT_GROUP ?? {};

    const accountGroupsWithAccess: AccountGroupWithAccess[] = accountGroups?.map((accountGroup) => {
      const acGroup = accountGroup.toJS() as AccountGroup;
      if (write?.includes(accountGroup.id)) return { ...acGroup, permission: 'WRITE' };
      if (read?.includes(accountGroup.id)) return { ...acGroup, permission: 'READ' };
      return { ...acGroup, permission: 'NONE' };
    });    

    const nextFullAccess = hasDatasetFullAccess(accountGroups, share);
    setAccountGroupsState(accountGroupsWithAccess);
    setFullAccess(nextFullAccess);
  }, [accountGroups, share]); 

  const areAllNone = !fullAccess && accountGroupsState.every((group) => group.permission === 'NONE');

  const handleClose = () => {
    dispatch(removeDialog(DIALOG_TYPE_ACCOUNT_SHARE));
  };

  const handleAccessChange = (id: string, selectedAccess: DatasetShareAccessLevel) => {
    const updated = accountGroupsState.map((accountGroup) => accountGroup.id === id ? { ...accountGroup, permission: selectedAccess } : accountGroup);
    setAccountGroupsState(updated);
  };

  const handleUpdate = () => {
    if (accountGroupsState) {
      const write = accountGroupsState
        .filter((accountGroup) => accountGroup.permission === 'WRITE')
        .map((accountGroup) => accountGroup.id);
      const read = accountGroupsState
        .filter((accountGroup) => accountGroup.permission === 'READ')
        .map((accountGroup) => accountGroup.id);
      const itemAccessControl = getItemAccessControl(isCFOUser, fullAccess, read, write);
      const shareUpdated = mkDatasetShare({ id: share.id, itemAccessControl });

      tracker.track(tracker.events.datasetSharing, {
        property: 'changePermission',
        ...(isCFOUser && getCFOEntityAccessTrackingProps(fullAccess, accountGroupsState.length, read, write)),
      });

      dispatch(datasetSharesActions.updateDatasetShare(shareUpdated));
    }
    handleClose();
  };

  return (
    <Dialog
      open
      sx={{
        '& .MuiPaper-root': {
          padding: '0 20px',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitleWrapper}>
        <Typography variant="h6" className={classes.title}>
          Change Permission
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        className={classes.closeButton}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 0, pb: 2.5 }}>
        <AccessTypes 
          accountGroups={accountGroupsState}
          fullAccess={fullAccess}
          handleFullAccessChange={(event) => setFullAccess(event.target.value === 'true')}
        />
        {
          !fullAccess && 
          <AccountGroupsShare 
            accountGroups={accountGroupsState}
            onAccessLevelChange={handleAccessChange}
          />
        }
      </DialogContent>
      <DialogActions
        className={classes.dialogAction}
      >
        <Button 
          onClick={handleClose}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleUpdate}
          disabled={areAllNone}
          sx={{ backgroundColor: 'action.surface' }}
        >
          UPDATE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageAccountShare;
